import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  CircularProgress,
  Button,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { FileDownload as FileDownloadIcon, PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import cuentaCorrienteService, {
  CuentaCorrienteComprobante,
  CuentaCorrienteSaldo
} from '../../services/cuentaCorrienteService';
import { formatCurrency } from '../../utils/formatters';
import * as XLSX from 'xlsx';
import ClienteSaldoSearch from '../../components/ventas/ClienteSaldoSearch';

const formatterFecha = new Intl.DateTimeFormat('es-AR', {
  dateStyle: 'short'
});

const ComposicionSaldo: React.FC = () => {
  const navigate = useNavigate();
  const { nroCliente } = useParams<{ nroCliente: string }>();
  const { enqueueSnackbar } = useSnackbar();
  
  const [comprobantes, setComprobantes] = useState<CuentaCorrienteComprobante[]>([]);
  const [clienteInfo, setClienteInfo] = useState<CuentaCorrienteSaldo | null>(null);
  const [nroClienteInput, setNroClienteInput] = useState<string>('');
  const [openClienteSearch, setOpenClienteSearch] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [saldoInicialPagina, setSaldoInicialPagina] = useState(0);

  const loadData = useCallback(async (clienteNum: number) => {
    try {
      setLoading(true);
      
      // Buscar el cliente en la lista de saldos usando searchTerm
      let clienteEncontrado: CuentaCorrienteSaldo | null = null;
      try {
        // Usar searchTerm para buscar directamente por número de cliente
        const saldosResponse = await cuentaCorrienteService.getSaldos({
          pageNumber: 1,
          pageSize: 100,
          searchTerm: clienteNum.toString()
        });
        
        // Buscar el cliente en los resultados (debería estar en la primera página con searchTerm)
        clienteEncontrado = saldosResponse.items.find(
          (item) => Number(item.nroCliente) === Number(clienteNum)
        ) || null;
      } catch (error) {
        console.error('Error al cargar los saldos:', error);
        enqueueSnackbar('Error al cargar los saldos del cliente.', { variant: 'error' });
      }

      if (!clienteEncontrado) {
        enqueueSnackbar('Cliente no encontrado o sin saldo.', { variant: 'error' });
        setClienteInfo(null);
        setComprobantes([]);
        setTotalCount(0);
        setSaldoInicialPagina(0);
        return;
      }
      
      setClienteInfo(clienteEncontrado);
      setNroClienteInput(clienteNum.toString());
      setPage(0); // Resetear página al cargar nuevo cliente
      setSaldoInicialPagina(0); // Resetear saldo inicial al cargar nuevo cliente
    } catch (error) {
      console.error('Error al obtener los datos:', error);
      enqueueSnackbar('No se pudieron cargar los datos.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (nroCliente) {
      loadData(Number(nroCliente));
    }
  }, [nroCliente, loadData]);

  // Función auxiliar para cargar todos los comprobantes en lotes de 50 (límite del backend)
  const loadAllComprobantes = useCallback(async (clienteNum: number): Promise<CuentaCorrienteComprobante[]> => {
    const maxPageSize = 50; // Límite del backend
    const allComprobantes: CuentaCorrienteComprobante[] = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      const response = await cuentaCorrienteService.getComprobantesCliente(clienteNum, {
        pageNumber: currentPage,
        pageSize: maxPageSize
      });

      allComprobantes.push(...response.items);

      // Si recibimos menos items que el pageSize, no hay más páginas
      if (response.items.length < maxPageSize) {
        hasMore = false;
      } else {
        currentPage++;
      }
    }

    return allComprobantes;
  }, []);

  // Cargar comprobantes con paginación del servidor
  const loadComprobantes = useCallback(async (clienteNum: number, pageNumber: number, pageSize: number) => {
    if (!clienteNum) return;
    
    try {
      setLoading(true);
      // Limitar el pageSize a máximo 50 según la restricción del backend
      const limitedPageSize = Math.min(pageSize, 50);
      const response = await cuentaCorrienteService.getComprobantesCliente(clienteNum, {
        pageNumber: pageNumber + 1, // El servidor usa 1-based, pero el componente usa 0-based
        pageSize: limitedPageSize
      });

      setComprobantes(response.items);
      setTotalCount(response.totalCount);

          // Calcular el saldo inicial de la página cargando todos los comprobantes anteriores
      let saldoInicial = 0;
      if (pageNumber > 0) {
        try {
          // Cargar todos los comprobantes anteriores para calcular el saldo inicial
          // El backend limita el pageSize a 50, así que cargamos en lotes si es necesario
          const totalComprobantesAnteriores = pageNumber * limitedPageSize;
          const maxPageSize = 50; // Límite del backend
          
          // Cargar comprobantes en lotes de 50
          for (let offset = 0; offset < totalComprobantesAnteriores; offset += maxPageSize) {
            const currentPageSize = Math.min(maxPageSize, totalComprobantesAnteriores - offset);
            const currentPageNumber = Math.floor(offset / maxPageSize) + 1;
            
            const comprobantesLote = await cuentaCorrienteService.getComprobantesCliente(clienteNum, {
              pageNumber: currentPageNumber,
              pageSize: currentPageSize
            });
            
            // Calcular el saldo acumulado de este lote
            comprobantesLote.items.forEach((item) => {
              saldoInicial += item.saldoDeb - item.saldoHab;
            });
          }
        } catch (error) {
          console.warn('No se pudieron cargar los comprobantes anteriores para calcular el saldo inicial:', error);
          // Si falla, el saldo inicial será 0
        }
      }
      setSaldoInicialPagina(saldoInicial);
    } catch (error) {
      console.error('Error al cargar los comprobantes:', error);
      enqueueSnackbar('No se pudieron cargar los comprobantes.', { variant: 'error' });
      setComprobantes([]);
      setTotalCount(0);
      setSaldoInicialPagina(0);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Cargar comprobantes cuando cambie la página, tamaño de página o cliente
  useEffect(() => {
    if (nroCliente && clienteInfo) {
      loadComprobantes(Number(nroCliente), page, rowsPerPage);
    }
  }, [nroCliente, clienteInfo, page, rowsPerPage, loadComprobantes]);

  const handleClienteSelect = (cliente: CuentaCorrienteSaldo) => {
    navigate(`/ventas/informes/composicion-saldo/${cliente.nroCliente}`);
  };

  const handleClienteSearch = async () => {
    const nroClienteNum = Number(nroClienteInput);
    if (!nroClienteNum || isNaN(nroClienteNum)) {
      enqueueSnackbar('Ingrese un número de cliente válido.', { variant: 'warning' });
      return;
    }
    navigate(`/ventas/informes/composicion-saldo/${nroClienteNum}`);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Limitar el pageSize a máximo 50 según la restricción del backend
    const limitedRowsPerPage = Math.min(newRowsPerPage, 50);
    setRowsPerPage(limitedRowsPerPage);
    setPage(0);
  };

  const handleExportExcel = async () => {
    if (totalCount === 0 || !nroCliente) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingExcel(true);

      // Cargar todos los comprobantes para la exportación (en lotes de 50)
      const allComprobantes = await loadAllComprobantes(Number(nroCliente));

      // Calcular saldo acumulado usando todos los comprobantes
      let saldoAcumulado = 0;
      const data = allComprobantes.map((item) => {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        return {
          'Fecha': item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          'Comprobante': item.comprobante,
          'Número': item.numero,
          'Importe': item.importe,
          'Debe': item.saldoDeb,
          'Haber': item.saldoHab,
          'Saldo Acumulado': saldoAcumulado,
          'Fecha Vencimiento': item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '',
          'Nro. Formulario': item.nroFormulario
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ComposicionSaldo');

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `composicion_saldo_cliente_${nroCliente}_${today}.xlsx`);
      enqueueSnackbar('Exportación a Excel completada.', { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('No se pudo exportar a Excel.', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    if (totalCount === 0 || !clienteInfo) {
      enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
      return;
    }

    try {
      setExportingPDF(true);

      if (totalCount === 0) {
        enqueueSnackbar('No hay datos para exportar.', { variant: 'warning' });
        return;
      }

      // Cargar todos los comprobantes para la exportación (en lotes de 50)
      const allComprobantes = await loadAllComprobantes(Number(nroCliente));

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      // Título
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Composición de Saldo - Cuenta Corriente', marginX, currentY);
      currentY += 24;

      // Información del cliente
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cliente: ${nroCliente} - ${clienteInfo.nombre}`, marginX, currentY);
      currentY += 16;
      doc.text(`Saldo Total: ${formatCurrency(clienteInfo.saldoTotal)}`, marginX, currentY);
      currentY += 20;

      // Calcular saldo acumulado para la tabla usando todos los comprobantes
      let saldoAcumulado = 0;
      const tableData = allComprobantes.map((item) => {
        saldoAcumulado += item.saldoDeb - item.saldoHab;
        return [
          item.fecha ? formatterFecha.format(new Date(item.fecha)) : '',
          item.comprobante || '',
          item.numero || '',
          formatCurrency(item.importe),
          formatCurrency(item.saldoDeb),
          formatCurrency(item.saldoHab),
          formatCurrency(saldoAcumulado),
          item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : ''
        ];
      });

      autoTable(doc, {
        startY: currentY,
        head: [['Fecha', 'Comprobante', 'Número', 'Importe', 'Debe', 'Haber', 'Saldo Acumulado', 'Fecha Venc.']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 90 },
          2: { cellWidth: 70 },
          3: { halign: 'right', cellWidth: 70 },
          4: { halign: 'right', cellWidth: 50 },
          5: { halign: 'right', cellWidth: 50 },
          6: { halign: 'right', cellWidth: 75 },
          7: { cellWidth: 70 }
        },
        tableWidth: 'auto'
      });

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `composicion_saldo_cliente_${nroCliente}_${today}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar('PDF descargado correctamente.', { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('No se pudo generar el PDF.', { variant: 'error' });
    } finally {
      setExportingPDF(false);
    }
  };

  // Calcular saldo acumulado para mostrar en la tabla
  const calcularSaldoAcumulado = (indexInPage: number): number => {
    // Empezar con el saldo inicial de la página
    let saldo = saldoInicialPagina;
    // Sumar los comprobantes de la página actual hasta el índice indicado
    for (let i = 0; i <= indexInPage; i++) {
      if (comprobantes[i]) {
        saldo += comprobantes[i].saldoDeb - comprobantes[i].saldoHab;
      }
    }
    return saldo;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título */}
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            Composición de Saldo
          </Typography>

          {/* Formulario de búsqueda */}
          <Card 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              bgcolor: '#fafafa'
            }}
          >
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Número de Cliente"
                    value={nroClienteInput}
                    onChange={(e) => setNroClienteInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleClienteSearch();
                      }
                    }}
                    helperText="Escriba el número para buscar automáticamente"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={() => setOpenClienteSearch(true)} 
                            edge="end" 
                            color="primary" 
                            title="Seleccionar cliente"
                            sx={{
                              borderRadius: 1.5,
                            }}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                {clienteInfo && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        value={clienteInfo.nombre}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Saldo Total"
                        value={formatCurrency(clienteInfo.saldoTotal)}
                        disabled
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& .MuiInputBase-input': {
                              fontWeight: 600,
                              color: '#1976d2',
                            }
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Botones de exportación */}
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            {clienteInfo && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcel}
                  disabled={loading || exportingExcel || comprobantes.length === 0}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPDF}
                  disabled={loading || exportingPDF || comprobantes.length === 0}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                    }
                  }}
                >
                  {exportingPDF ? 'Generando...' : 'Exportar PDF'}
                </Button>
              </Stack>
            )}
          </Box>

          {/* Mensaje cuando no hay cliente seleccionado */}
          {!clienteInfo && !loading && (
            <Card 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
                bgcolor: '#fafafa'
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Seleccione un cliente para ver la composición de su saldo
              </Typography>
            </Card>
          )}

          {/* Tabla de comprobantes */}
          {clienteInfo && (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <CircularProgress />
                </Box>
              ) : comprobantes.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} p={3}>
                  <Typography>No hay comprobantes para este cliente.</Typography>
                </Box>
              ) : (
                <>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Número</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Debe</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Haber</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Saldo Acumulado</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Fecha Venc.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comprobantes.map((item, index) => {
                        const saldoAcumulado = calcularSaldoAcumulado(index);
                        return (
                          <TableRow 
                            key={`${item.numero}-${index}`}
                            sx={{
                              '&:hover': {
                                bgcolor: '#f9f9f9',
                              },
                            }}
                          >
                            <TableCell>
                              {item.fecha ? formatterFecha.format(new Date(item.fecha)) : '-'}
                            </TableCell>
                            <TableCell>{item.comprobante || '-'}</TableCell>
                            <TableCell>{item.numero || '-'}</TableCell>
                            <TableCell align="right">{formatCurrency(item.importe)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoDeb)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.saldoHab)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {formatCurrency(saldoAcumulado)}
                            </TableCell>
                            <TableCell>
                              {item.fechaVenc ? formatterFecha.format(new Date(item.fechaVenc)) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                      borderTop: '1px solid #e0e0e0',
                    }}
                  />
                </>
              )}
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ClienteSaldoSearch
        open={openClienteSearch}
        onClose={() => setOpenClienteSearch(false)}
        onSelect={handleClienteSelect}
      />
    </Box>
  );
};

export default ComposicionSaldo;

